import { Button } from '@/components/ui/Button';
import { Field, Textarea } from '@/components/ui/Field';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { useCreateFeedback } from '@/features/feedback/api';
import { MessageSquare, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Okno „Zgłoś uwagę” — otwierane z menu profilu (wczesniej plywajacy przycisk
 * w rogu, ktory zaslanial tresc na telefonie — audyt 2026-09-29). Zapisuje
 * uwage z adresem biezacej strony; admin widzi liste w /uwagi.
 */
export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const location = useLocation();
  const create = useCreateFeedback();
  const [message, setMessage] = useState('');
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // `ref={el => el?.focus()}` nie zadziala: natywny <dialog> otwiera sie dopiero po
  // zamontowaniu pol. Ten efekt odpala sie po efekcie bazy (rodzic), wiec okno jest otwarte.
  useEffect(() => {
    messageRef.current?.focus();
  }, []);

  const send = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 3) {
      toast.error('Uwaga jest za krótka (min. 3 znaki)');
      return;
    }
    try {
      await create.mutateAsync({
        pageUrl: location.pathname + location.search,
        pageTitle: document.title,
        message: trimmed,
      });
      toast.success('Dzięki! Uwaga zapisana — admin zobaczy ją w panelu.');
      setMessage('');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Błąd zapisu uwagi');
    }
  };

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      size="md"
      icon={MessageSquare}
      title="Zgłoś uwagę"
      footer={
        <>
          <Button className="max-sm:flex-1" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            icon={Send}
            className="max-sm:flex-1"
            onClick={send}
            loading={create.isPending}
            disabled={message.trim().length < 3}
          >
            Wyślij uwagę
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-border bg-bg/50 p-3">
          <div className="text-xs font-medium text-fg-muted">Strona</div>
          <div className="mt-0.5 truncate font-mono text-xs text-fg-muted">
            {location.pathname}
            {location.search}
          </div>
        </div>

        <Field
          label="Co zauważono? Co warto zmienić?"
          hint={
            <span className="flex items-center justify-between gap-2">
              <span>Będzie zapisane razem z linkiem do tej strony.</span>
              <span className="shrink-0">{message.length} / 5000</span>
            </span>
          }
        >
          {(fieldId) => (
            <Textarea
              id={fieldId}
              ref={messageRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Np. nie podoba mi się ta sekcja, bo… / brakuje przycisku X / styl jest…"
              rows={6}
              maxLength={5000}
              className="resize-none"
            />
          )}
        </Field>
      </div>
    </ResponsiveModal>
  );
}
